// #region Imports
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SnippetFolderIcon from "@mui/icons-material/SnippetFolder";
import UndoIcon from "@mui/icons-material/Undo";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import FeeScheduleBillingCode from "_shared/fee-schedules/categories/billing-codes/fee-schedule-billing-code";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  type MRT_Row,
  MRT_TableInstance,
  MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FeeScheduleCategoryMaskedCurrency } from "work/_shared/masked-fields";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import FeeRedlineInput from "work/proposals/redlining/fee-schedule-redline/fee-redline/_views/fee-redline-input";
import { FeeScheduleCategoryRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-category-redline";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import TextRedlineInput from "work/proposals/redlining/field-redlines/_views/text-redline-input";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";
import { MRTNoMemo } from "_shared/_views/tables/table";
// #endregion

// #region Styles
const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
  marginBottom: theme.spacing(2),
  marginLeft: theme.spacing(1)
}));
const RedlineText = styled(TextRedlineInput)(() => ({
  margin: 0,
  minWidth: "unset",
  width: "100%",
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const Root = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
});
// #endregion

// #region Types/Props
type FeeScheduleRedlineSelectionProps = {
  className?: string;
  feeScheduleRedline: FeeScheduleRedline;
  disableEditing?: boolean;
  commentThreads: CommentThread[];
  activeReviewField: ProposalField | undefined;
  onCommentsClicked: (field: ProposalField, name?: string) => void;
  onCategoryClicked?: (categoryId: Guid) => void;
  onCreateCategoryClicked: () => void;
  onFeeScheduleRedlineChange: (
    newRedline: FeeScheduleRedline,
    traversalFieldOverride?: ProposalField | null
  ) => void;
  onExistingCategoryClicked: () => void;
};
// #endregion

const FeeScheduleRedlineSelection = (props: Readonly<FeeScheduleRedlineSelectionProps>) => {
  const {
    className,
    feeScheduleRedline,
    commentThreads,
    disableEditing,
    activeReviewField,
    onCommentsClicked,
    onCategoryClicked,
    onCreateCategoryClicked,
    onExistingCategoryClicked,
    onFeeScheduleRedlineChange,
  } = props;

  // #region Refs
  const prevVisibilityRef = useRef<Record<string, boolean> | null>(null);
  const prevSizingRef = useRef<Record<string, number> | null>(null);

  // #region Lifecycle
  useEffect(() => {
    const target = document.getElementById(activeReviewField?.id?.value ?? "");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeReviewField]);
  // #endregion

  // #region Functions
  const handleRemoveClicked = useCallback(
    (categoryId: Guid) => {
      const newRedline = feeScheduleRedline.removeEntryByFieldId(categoryId);
      onFeeScheduleRedlineChange(newRedline, newRedline.field);
    },
    [feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  const enterFocusMode = useCallback((table: MRT_TableInstance<FeeScheduleCategoryRedline>, columnId: string) => {
    const tableState = table.getState();

    // Snapshot existing state
    prevVisibilityRef.current ??= { ...tableState.columnVisibility };
    prevSizingRef.current ??= { ...tableState.columnSizing };

    const keepVisible = new Set<string>([columnId, "mrt-row-actions", "name"]);

    // Build column visibility map
    const nextVisibility: Record<string, boolean> = {};
    table.getAllLeafColumns().forEach((col) => {
      nextVisibility[col.id] = keepVisible.has(col.id);
    });

    table.setColumnVisibility(nextVisibility);
  }, []);

  const exitFocusMode = useCallback((table: MRT_TableInstance<FeeScheduleCategoryRedline>) => {
    if (prevVisibilityRef.current) {
      table.setColumnVisibility(prevVisibilityRef.current);
      prevVisibilityRef.current = null;
    }
    if (prevSizingRef.current) {
      table.setColumnSizing(prevSizingRef.current);
      prevSizingRef.current = null;
    }
  }, []);
  // #endregion

  // #region Billing Code Cell
  const renderBillingCodeRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.billingCodeRedline) {
        console.error("Category Redline billing code is malformed", categoryRedline);
        return;
      }
      return (
        <RedlineText
          variant="standard"
          size="small"
          margin="none"
          fullWidth
          hideLabel
          showPlaceholder
          textRedline={categoryRedline.billingCodeRedline}
          readOnly={disableEditing || categoryRedline.isRemoved}
          hideUndoButton={true}
          hideAcceptRejectButtons={true}
          hideComments={true}
          fieldConstructor={FeeScheduleBillingCode}
          onShowTextInput={() => enterFocusMode(table, "billingCode")}
          onHideTextInput={() => exitFocusMode(table)}
          onTextFieldRedlineChange={async (
            newTextRedline: FieldRedline<FeeScheduleBillingCode>,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
            if (!existingCategory || !newTextRedline.currentEntry) {
              console.error("category redline is malformed", categoryRedline);
              return;
            }
            const updatedCategory =
              existingCategory instanceof FeeScheduleCategory
                ? FeeScheduleCategory.create({
                  name: existingCategory.name,
                  description: existingCategory.description,
                  fee: existingCategory.fee,
                  billingCode: newTextRedline.currentEntry,
                  id: existingCategory.id,
                })
                : new FeeScheduleCategoryMaskedCurrency({
                  id: existingCategory.id,
                  name: existingCategory.name,
                  description: existingCategory.description,
                  billingCode: newTextRedline.currentEntry,
                });
            const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
            onFeeScheduleRedlineChange(newRedline, traversalFieldOverride);
          }}
        />
      );
    },
    [disableEditing, enterFocusMode, exitFocusMode, feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  // #region Description Cell
  const renderDescriptionRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.descriptionRedline) {
        console.error("Category Redline description is malformed", categoryRedline);
        return;
      }
      return (
        <RedlineText
          variant="standard"
          size="small"
          margin="none"
          fullWidth
          hideLabel
          showPlaceholder
          hideUndoButton={true}
          hideAcceptRejectButtons={true}
          textRedline={categoryRedline.descriptionRedline}
          fieldConstructor={FeeScheduleCategoryDescription}
          onShowTextInput={() => enterFocusMode(table, "description")}
          onHideTextInput={() => exitFocusMode(table)}
          onTextFieldRedlineChange={async (
            newTextRedline: FieldRedline<FeeScheduleCategoryDescription>,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
            if (!existingCategory || !newTextRedline.currentEntry) {
              console.error("category redline is malformed", categoryRedline);
              return;
            }
            const updatedCategory =
              existingCategory instanceof FeeScheduleCategory
                ? FeeScheduleCategory.create({
                  name: existingCategory.name,
                  description: newTextRedline.currentEntry,
                  fee: existingCategory.fee,
                  billingCode: existingCategory.billingCode,
                  id: existingCategory.id,
                })
                : new FeeScheduleCategoryMaskedCurrency({
                  id: existingCategory.id,
                  name: existingCategory.name,
                  description: newTextRedline.currentEntry,
                  billingCode: existingCategory.billingCode,
                });

            const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
            onFeeScheduleRedlineChange(newRedline, traversalFieldOverride);
          }}
          readOnly={disableEditing || categoryRedline.isRemoved}
          hideComments={true}
        />
      );
    },
    [disableEditing, enterFocusMode, exitFocusMode, feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  // #region Fee Cell
  const renderFeeRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (
        rowData.originalEntry instanceof FeeScheduleCategoryMaskedCurrency ||
        rowData.revisedEntry instanceof FeeScheduleCategoryMaskedCurrency
      ) {
        return <em>Masked</em>;
      }
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.feeRedline) {
        console.error("Category Redline fee is malformed", categoryRedline);
        return;
      }

      return (
        <FeeRedlineInput
          variant="standard"
          size="small"
          margin="none"
          categoryRedline={categoryRedline}
          readOnly={disableEditing || categoryRedline.isRemoved}
          onShowTextInput={() => enterFocusMode(table, "fee")}
          onHideTextInput={() => exitFocusMode(table)}
          onFeeChange={(newFee: Fee) => {
            const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
            if (!existingCategory) {
              console.error("category redline is malformed", categoryRedline);
              return;
            }
            const updatedCategory =
              existingCategory instanceof FeeScheduleCategory
                ? FeeScheduleCategory.create({
                  name: existingCategory.name,
                  description: existingCategory.description,
                  fee: newFee,
                  billingCode: existingCategory.billingCode,
                  id: existingCategory.id,
                })
                : new FeeScheduleCategoryMaskedCurrency({
                  id: existingCategory.id,
                  name: existingCategory.name,
                  description: existingCategory.description,
                  billingCode: existingCategory.billingCode,
                });

            const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
            onFeeScheduleRedlineChange(newRedline);
          }}
        />
      );
    },
    [disableEditing, enterFocusMode, exitFocusMode, feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  // #region Name Cell
  const renderNameRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.nameRedline) {
        console.error("Category Redline Name is malformed", categoryRedline);
        return;
      }
      return categoryRedline.isAccepted && categoryRedline.isRemoved ? (
        "Removed"
      ) : (
        <RedlineText
          id={categoryRedline.field.id?.value}
          bold={true}
          variant="standard"
          size="small"
          margin="none"
          hideLabel
          showPlaceholder
          hideUndoButton={true}
          hideAcceptRejectButtons={true}
          fullWidth
          textRedline={categoryRedline.nameRedline}
          fieldConstructor={FeeScheduleCategoryName}
          onShowTextInput={() => enterFocusMode(table, "name")}
          onHideTextInput={() => exitFocusMode(table)}
          onTextFieldRedlineChange={(
            newTextRedline: FieldRedline<FeeScheduleCategoryName>,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            const duplicateName = feeScheduleRedline.redlines.find((redline) => {
              if (!redline.nameRedline) {
                console.error("Category Redline Name is malformed", redline);
                return false;
              }
              return redline.nameRedline.currentEntry === newTextRedline.currentEntry;
            });
            if (duplicateName) {
              enqueueSnackbar("A category with the same name already exists", {
                variant: "warning",
              });
              return;
            }

            const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
            if (!existingCategory || !newTextRedline.currentEntry) {
              console.error("category redline is malformed", categoryRedline);
              return;
            }
            const updatedCategory =
              existingCategory instanceof FeeScheduleCategory
                ? FeeScheduleCategory.create({
                  id: existingCategory.id,
                  name: newTextRedline.currentEntry,
                  description: existingCategory.description,
                  fee: existingCategory.fee,
                  billingCode: existingCategory.billingCode,
                })
                : new FeeScheduleCategoryMaskedCurrency({
                  id: existingCategory.id,
                  name: newTextRedline.currentEntry,
                  description: existingCategory.description,
                  billingCode: existingCategory.billingCode,
                });

            const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
            onFeeScheduleRedlineChange(newRedline, traversalFieldOverride);
          }}
          readOnly={disableEditing || categoryRedline.isRemoved}
          hideComments={true}
        />
      );
    },
    [disableEditing, enterFocusMode, exitFocusMode, feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  // #region Columns
  const columnHelper = useMemo(() => createMRTColumnHelper<FeeScheduleCategoryRedline>(), []);
  const columns: Array<ReturnType<typeof columnHelper.display>> = useMemo(
    () => [
      columnHelper.display({
        header: "Name",
        id: "name",
        Cell: ({ table, row }) => renderNameRedline(table, row.original),
      }),
      columnHelper.display({
        header: "Description",
        id: "description",
        Cell: ({ table, row }) => renderDescriptionRedline(table, row.original),
      }),
      columnHelper.display({
        header: "Fee",
        id: "fee",
        Cell: ({ table, row }) => renderFeeRedline(table, row.original),
      }),
      columnHelper.display({
        header: "Billing Code",
        id: "billingCode",
        Cell: ({ table, row }) => renderBillingCodeRedline(table, row.original),
      }),
    ],
    [columnHelper, renderBillingCodeRedline, renderDescriptionRedline, renderFeeRedline, renderNameRedline],
  );

  // #region Row Actions
  const renderRowActions = useCallback(
    (props: { row: MRT_Row<FeeScheduleCategoryRedline> }) => {
      const { row } = props;
      if (!row.original.field.id) return null;

      const category = Object.assign(new FeeScheduleCategoryRedline(row.original.field.id, null, null), row.original);

      if (!category) return null;
      if (disableEditing) {
        return (
          <CommentsButton
            commentThreads={commentThreads}
            field={category.field}
            toolTip="Comments"
            onCommentsClicked={() => {
              onCommentsClicked?.(
                category.field,
                category?.currentEntry?.name?.value ?? category?.revisedEntry?.name?.value,
              );
            }}
          />
        );
      }

      return (
        <>
          {category.isResolved && !category.isRemoved && !category.isNewlyAdded && (
            <Tooltip title="Remove Fee">
              <span>
                <ActionButton onClick={() => category.field.id && handleRemoveClicked(category.field.id)}>
                  <DeleteOutlineIcon color="error" />
                </ActionButton>
              </span>
            </Tooltip>
          )}
          {!category.isResolved && (
            <>
              <Tooltip title="Accept Changes">
                <span>
                  <ActionButton
                    onClick={() => {
                      onFeeScheduleRedlineChange(feeScheduleRedline.updateRedline(category.accept()));
                    }}
                  >
                    <CheckIcon color="success" />
                  </ActionButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject Changes">
                <span>
                  <ActionButton
                    onClick={() => {
                      onFeeScheduleRedlineChange(feeScheduleRedline.updateRedline(category.reject()));
                    }}
                  >
                    <CloseIcon color="error" />
                  </ActionButton>
                </span>
              </Tooltip>
            </>
          )}
          {category.canBeUndone && (
            <Tooltip title="Undo Changes">
              <span>
                <ActionButton
                  onClick={() => {
                    if (category.field.id) {
                      onFeeScheduleRedlineChange(feeScheduleRedline.undoRedlineById(category.field.id), category.field);
                    }
                  }}
                >
                  <UndoIcon color="secondary" />
                </ActionButton>
              </span>
            </Tooltip>
          )}
          <CommentsButton
            commentThreads={commentThreads}
            field={category.field}
            isActive={activeReviewField?.isEqualTo(category.field)}
            toolTip="Comments"
            onCommentsClicked={() => {
              onCommentsClicked?.(
                category.field,
                category?.currentEntry?.name?.value ?? category?.revisedEntry?.name?.value,
              );
            }}
          />
        </>
      );
    },
    [
      disableEditing,
      commentThreads,
      activeReviewField,
      onCommentsClicked,
      handleRemoveClicked,
      onFeeScheduleRedlineChange,
      feeScheduleRedline,
    ],
  );

  // #region GetRowId
  const getRowId = useCallback((row: FeeScheduleCategoryRedline) => {
    return row.field.id?.value ?? Guid.generate().value;
  }, []);

  // #region muiTableProps
  const muiTableProps = useCallback((props: {
    table: MRT_TableInstance<FeeScheduleCategoryRedline>
  }
  ) => {
    const { table } = props;
    return ({
      sx: {
        borderSpacing: table.getRowModel().rows.some((row) => row.id === activeReviewField?.id?.value) ? "3px" : 0,
      },
    });
  }, [activeReviewField]);

  // #region muiTableBodyRowProps
  const muiTableBodyRowProps = useCallback((props: { row: MRT_Row<FeeScheduleCategoryRedline> }) => {
    const { row } = props;
    return {
      hover: false,
      onClick: () => {
        if (!row.original.field.id) return;
        onCategoryClicked?.(row.original.field.id);
      },
      sx: {
        animation: row.original.field?.isEqualTo(activeReviewField) ? `${pulseBorder} 1.5s infinite` : "none",
      },
    }
  }, [activeReviewField, onCategoryClicked]);

  // #region renderEmptyRowsFallback
  const renderEmptyRowsFallback = useCallback(() => (
    <Box>
      <Typography sx={{ p: 4, display: "flex", justifyContent: "center" }} variant="body1" color="textSecondary">
        No fee schedule categories
      </Typography>
    </Box>
  ), []);

  // #region renderTopToolbarCustomActions
  const renderTopToolbarCustomActions = useCallback(() => (
    <ActionsContainer>
      {!disableEditing && (
        <>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={onCreateCategoryClicked}
          >
            Add New Category
          </Button>
          <Button
            startIcon={<SnippetFolderIcon />}
            variant="outlined"
            size="small"
            onClick={onExistingCategoryClicked}
          >
            Add Existing Category
          </Button>
          {!feeScheduleRedline.isResolved && (
            <>
              <Button
                color="success"
                size="small"
                onClick={() => {
                  onFeeScheduleRedlineChange(feeScheduleRedline.acceptAll());
                }}
                startIcon={<CheckIcon color="success" />}
              >
                Accept All
              </Button>
              <Button
                color="error"
                size="small"
                onClick={() => {
                  onFeeScheduleRedlineChange(feeScheduleRedline.rejectAll());
                }}
                startIcon={<CloseIcon color="error" />}
              >
                Reject All
              </Button>
            </>
          )}
          {feeScheduleRedline.canBeUndone && (
            <Button
              color="secondary"
              size="small"
              onClick={() => {
                onFeeScheduleRedlineChange(feeScheduleRedline.undoAll(), null);
              }}
              startIcon={<UndoIcon color="secondary" />}
            >
              Undo All
            </Button>
          )}
        </>
      )}
    </ActionsContainer>
  ), [
    disableEditing,
    onCreateCategoryClicked,
    onExistingCategoryClicked,
    feeScheduleRedline,
    onFeeScheduleRedlineChange
  ]);

  // #region renderToolbarInternalActions
  const renderToolbarInternalActions = useCallback((props: { table: MRT_TableInstance<FeeScheduleCategoryRedline> }) => {
    const { table } = props;
    return (
      <Box>
        <ToggleGlobalFilterButton table={table} />
        <ToggleFiltersButton table={table} />
        <ShowHideColumnsButton table={table} />
        <ToggleDensePaddingButton table={table} />
        <ToggleFullScreenButton table={table} />
        <CommentsButton
          commentThreads={commentThreads}
          field={ProposalField.FeeSchedule}
          isActive={activeReviewField?.isEqualTo(ProposalField.FeeSchedule)}
          toolTip="Comments"
          onCommentsClicked={() => {
            onCommentsClicked?.(ProposalField.FeeSchedule);
          }}
        />
      </Box>
    )
  }, [commentThreads, activeReviewField, onCommentsClicked]);

  // #region muiTableContainerProps
  const muiTableContainerProps = useCallback((props: { table: MRT_TableInstance<FeeScheduleCategoryRedline> }) => {
    const { table } = props;
    const top = table.refs.topToolbarRef.current?.offsetHeight ?? 0;
    const bottom = table.refs.bottomToolbarRef.current?.offsetHeight ?? 0;

    return {
      sx: {
        height: `calc(100% - ${top}px - ${bottom}px)`,
        minHeight: 0,
        overflowY: "auto",
      },
    }
  }, []);

  // #region Table Options
  const tableOptions: MRT_TableOptions<FeeScheduleCategoryRedline> = useMemo(
    () => ({
      columns,
      data: feeScheduleRedline.redlines,
      enableKeyboardShortcuts: false,
      muiPaginationProps: {
        color: "primary",
        shape: "rounded",
        showRowsPerPage: false,
        variant: "outlined",
      },
      paginationDisplayMode: "pages",
      enableRowSelection: false,
      enableTableHead: true,
      getRowId,
      initialState: {
        showColumnFilters: false,
        columnPinning: { right: ["mrt-row-actions"] },
        density: "comfortable",
      },
      enableColumnPinning: true,
      manualFiltering: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      enableRowActions: true,
      muiTableProps,
      muiTableBodyRowProps,
      renderEmptyRowsFallback,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
      renderRowActions: renderRowActions,
      muiTableContainerProps,
      muiTablePaperProps: {
        elevation: 0,
        sx: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        },
      },
    }),
    [
      columns, 
      feeScheduleRedline.redlines, 
      getRowId, 
      muiTableProps, 
      muiTableBodyRowProps, 
      renderEmptyRowsFallback, 
      renderTopToolbarCustomActions, 
      renderToolbarInternalActions, 
      renderRowActions, 
      muiTableContainerProps
    ]
  );

  // #region Render
  return (
    <Root className={className}>
      <MRTNoMemo<FeeScheduleCategoryRedline> options={tableOptions} />
    </Root>
  );
};

export default FeeScheduleRedlineSelection;
